<template>
  <v-card flat>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-card-text class="px-0">
      <v-tabs v-model="tab">
        <v-tab :key="CONTRACT_DETAILS">
          Contract Details
        </v-tab>
        <v-tab :key="COMMISSIONS">
          Commissions
        </v-tab>
        <v-tab :key="EXPENSES">
          Expenses
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item :key="CONTRACT_DETAILS">
          <v-row class="mt-3">
            <v-col cols="12" md="6">
              <v-row>
                <v-col cols="12">
                  <v-card-title class="px-0 py-0">Contract</v-card-title>
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Job Title"
                    v-model="assignment.jobTitle"
                    :readonly="isReadOnlyAccess"
                    outlined
                    dense
                    :rules="[Required('Job Title')]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <FormattedNumberField
                    label="Hours Worked Monthly"
                    :value="assignment.workedHoursAverage"
                    :required="true"
                    :readonly="isReadOnlyAccess"
                    :dense="true"
                    @valueChange="(newValue) => this.assignment.workedHoursAverage = newValue"
                  ></FormattedNumberField>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <PayFrequenciesDropdownList
                    :isRequired="true"
                    :payFrequency="assignment.payFrequencyId"
                    :readonly="isReadOnlyAccess"
                    :dense="true"
                    @payFrequencyChangeHandler="(newValue) => this.assignment.payFrequencyId = newValue"
                  ></PayFrequenciesDropdownList>
                </v-col>
                <v-col cols="12" sm="6">
                  <ContractTypesDropDownList
                    :id="assignment.contractTypeId"
                    :isRequired="true"
                    :readonly="isReadOnlyAccess"
                    :dense="true"
                    @change="(newValue) => {
                      this.assignment.contractTypeId = newValue
                      this.assignment.lengthOfContract = null
                      this.assignment.conversionFee = null
                    }"
                  ></ContractTypesDropDownList>
                </v-col>
              </v-row>
              <v-row v-if="isContractToHire">
                <v-col cols="12" sm="6">
                  <FormattedNumberField
                    label="Length of Contract (Months)"
                    :value="assignment.lengthOfContract"
                    :readonly="isReadOnlyAccess"
                    :rules="[isLengthOfContractRequired]"
                    :dense="true"
                    :onlyInt="true"
                    :validateOnBlur="true"
                    @valueChange="(newValue) => this.assignment.lengthOfContract = newValue"
                  ></FormattedNumberField>
                </v-col>
                <v-col cols="12" sm="6">
                  <FormattedNumberField
                    label="Conversion Fee"
                    :value="assignment.conversionFee"
                    :readonly="isReadOnlyAccess"
                    :rules="[isConversionFeeRequired]"
                    :dense="true"
                    :validateOnBlur="true"
                    prepend-inner-icon="attach_money"
                    @valueChange="(newValue) => this.assignment.conversionFee = newValue"
                  ></FormattedNumberField>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <SimpleDatePicker
                    label="Start Date"
                    :value="assignment.startAt"
                    :isRequired="true"
                    :readonly="isReadOnlyAccess"
                    :dense="true"
                    @selected-date-change="(newValue) => this.assignment.startAt = newValue"
                  ></SimpleDatePicker>
                </v-col>
                <v-col cols="12" sm="6">
                  <SimpleDatePicker
                    label="End Date"
                    :value="assignment.endAt"
                    :isRequired="isContractToHire || !assignment.active"
                    :clearable="!isReadOnlyAccess"
                    :readonly="isReadOnlyAccess"
                    :dense="true"
                    :min-date="assignment.startAt"
                    :min-date-enable="true"
                    @selected-date-change="endDateChangeHandler"
                  ></SimpleDatePicker>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-radio-group
                    label="Consultant Type"
                    class="mt-0"
                    v-model="assignment.employeeTypeId"
                    :rules="[isFilledOut()]"
                    :readonly="isReadOnlyAccess"
                    dense
                    @change="() => {this.assignment.corporateName = null; this.assignment.ein = null;}"
                  >
                    <v-radio class="my-0" label="1099" :value="INDEPENDENT_CONTRACTOR"></v-radio>
                    <v-radio class="my-0" label="C2C" :value="CORP_TO_CORP"></v-radio>
                    <v-radio class="my-0" label="W2 no Benefits" :value="EMPLOYEE_NO_BENEFITS"></v-radio>
                    <v-radio class="my-0" label="W2 + Benefits" :value="EMPLOYEE_WITH_BENEFITS"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" sm="6" v-if="isC2C">
                  <v-row>
                    <v-col cols="12" class="pt-5">
                      <v-text-field
                        label="Corp. Name"
                        v-model="assignment.corporateName"
                        validate-on-blur
                        :rules="[isCorporateNameRequired]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" class="py-0">
                      <v-text-field
                        label="EIN"
                        v-model="assignment.ein"
                        validate-on-blur
                        :rules="[isEINRequired]"
                        outlined
                        dense
                        :maxlength="10"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <TimeSheetTypesDropdown
                    label="Time Sheet Type"
                    :value="assignment.timeSheetType"
                    :isRequired="true"
                    :readonly="isReadOnlyAccess"
                    :dense="true"
                    @typeChange="(newValue) => this.assignment.timeSheetType = newValue"
                  ></TimeSheetTypesDropdown>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-if="assignment.timeSheetType === 3"
                    label="Clock In/Clock Out IP Address"
                    v-model="assignment.clockIPAddress"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
            <v-divider vertical></v-divider>
            <v-col cols="12" md="6">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-row>
                    <v-col cols="12" class="px-3">
                      <v-card-title class="px-0 py-0">
                        Pay Details
                      </v-card-title>
                    </v-col>
                  </v-row>
                  <v-row class="mt-0">
                    <v-col cols="12">
                      <FormattedNumberField
                        label="Pay Rate"
                        :value="assignment.payRate"
                        prepend-inner-icon="attach_money"
                        :dense="true"
                        :required="true"
                        :readonly="isReadOnlyAccess"
                        @valueChange="(newValue) => this.assignment.payRate = newValue"
                        @blur="setPayRateType"
                      ></FormattedNumberField>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <PayRateTypesDropdownList
                        :isRequired="true"
                        :payRateType="assignment.payRateTypeId"
                        :readonly="isReadOnlyAccess"
                        :dense="true"
                        @payRateTypeChangeHandler="payRateTypeChangeHandler"
                      >
                      </PayRateTypesDropdownList>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <FormattedNumberField
                        label="Monthly Total Pay Rate"
                        :value="totalPayRate"
                        :readonly="true"
                        :hint="totalPayRateHint"
                        :persistent-hint="!!totalPayRateHint"
                        :dense="true"
                        prepend-inner-icon="attach_money"
                      ></FormattedNumberField>
                    </v-col>
                  </v-row>
                </v-col>
                <v-divider vertical></v-divider>
                <v-col cols="12" sm="6">
                  <v-row>
                    <v-col cols="12" class="px-3">
                      <v-card-title class="px-0 py-0">
                        Bill Details
                      </v-card-title>
                    </v-col>
                  </v-row>
                  <v-row class="mt-0">
                    <v-col cols="12">
                      <FormattedNumberField
                        label="Bill Rate"
                        :value="assignment.billRate"
                        :required="true"
                        :readonly="isReadOnlyAccess"
                        prepend-inner-icon="attach_money"
                        :dense="true"
                        @valueChange="(newValue) => this.assignment.billRate = newValue"
                        @blur="setBillRateType"
                      ></FormattedNumberField>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <BillRateTypesDropdownList
                        :isRequired="true"
                        :billRateType="assignment.billRateTypeId"
                        :readonly="isReadOnlyAccess"
                        :dense="true"
                        @billRateTypeChangeHandler="(newValue) => this.assignment.billRateTypeId = newValue"
                      ></BillRateTypesDropdownList>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <FormattedNumberField
                        label="Monthly Net Bill Rate"
                        :value="netBillRate"
                        :readonly="true"
                        :hint="netBillRateHint"
                        :persistent-hint="!!netBillRateHint"
                        :dense="true"
                        prepend-inner-icon="attach_money"
                      ></FormattedNumberField>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" class="pb-0">
                  <FormattedNumberField
                    label="Markup %"
                    :value="markupPercentage"
                    :readonly="true"
                    :dense="true"
                    appendIcon="percent"
                    :hideDetails="true"
                  ></FormattedNumberField>
                </v-col>
                <v-col cols="6" class="pb-0">
                  <FormattedNumberField
                    label="Monthly Gross Profit"
                    :value="grossProfit"
                    :readonly="true"
                    :hint="grossProfitHint"
                    :persistent-hint="!!grossProfitHint"
                    :dense="true"
                    prepend-inner-icon="attach_money"
                  ></FormattedNumberField>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="pt-0" cols="12" sm="4">
                  <v-checkbox
                    v-if="isHourlyPaid"
                    label="Calculate Automatic OT"
                    v-model="assignment.overtimeEligible"
                    :readonly="isReadOnlyAccess"
                    dense
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="4">
                  <FormattedNumberField
                    v-if="assignment.overtimeEligible"
                    label="Week Threshold (Hours)"
                    :value="assignment.overtimeThreshold"
                    :dense="true"
                    :required="assignment.overtimeEligible"
                    :readonly="isReadOnlyAccess"
                    @valueChange="(newValue) => this.assignment.overtimeThreshold = newValue"
                  ></FormattedNumberField>
                </v-col>
                <v-col cols="12" sm="4">
                  <FormattedNumberField
                    v-if="assignment.overtimeEligible"
                    label="Rate"
                    :value="assignment.overtimeRate"
                    :dense="true"
                    :required="assignment.overtimeEligible"
                    :readonly="isReadOnlyAccess"
                    prepend-inner-icon="attach_money"
                    @valueChange="(newValue) => this.assignment.overtimeRate = newValue"
                  ></FormattedNumberField>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-checkbox
                    label="Contractor Commission Enabled"
                    v-model="assignment.contractorCommissionEnabled"
                    :readonly="isReadOnlyAccess || !canSetContractorCommission"
                    dense
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="6">
                  <FormattedNumberField
                    v-if="assignment.contractorCommissionEnabled"
                    label="Markup %"
                    appendIcon="percent"
                    :value="assignment.contractorCommissionMarkup"
                    :readonly="isReadOnlyAccess || !canSetContractorCommission"
                    :dense="true"
                    @valueChange="(newValue) => this.assignment.contractorCommissionMarkup = newValue"
                  ></FormattedNumberField>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-btn
                    block
                    outlined
                    color="primary"
                    @click="openMarkupCalculator"
                  >
                    Markup Calc.
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item :key="COMMISSIONS">
          <v-row class="my-5">
            <v-col cols="12">
              <CommissionsCollector
                ref="commissionCollector"
                commissionComponent="CommissionInput"
                commissionType="commission"
                :commissions="assignment.commissions"
                :readonly="isReadOnlyAccess"
                @commissionsChange="handleCommissionsChange"
              ></CommissionsCollector>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item :key="EXPENSES">
          <v-row class="my-5">
            <v-col cols="12">
              <ExpensesCard
                cardTitle="Expenses"
                :expensesList="assignment.expenses"
                :readonly="isReadOnlyAccess"
                :showActive="false"
                :showExpenseAmountType="true"
                @expensesListChange="(newValue) => this.assignment.expenses = newValue"
              ></ExpensesCard>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
    </v-form>
    <MessageBox
      ref="messageBox"
      message="There are required fields missing"
      color="error"
      closeTextColor="white"
    ></MessageBox>
    <MarkupCalculator ref="markupCalculator"></MarkupCalculator>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { CommissionsCollector } from '@/components/commissions'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { ContractTypesDropDownList } from '@/components/contractTypes'
import { PayFrequenciesDropdownList } from '@/components/payFrequencies'
import Required from '@/lib/formValidations/required'
import { ExpensesCard } from '@/components/expenses'
import {
  EMPLOYEE_WITH_BENEFITS,
  EMPLOYEE_NO_BENEFITS,
  CORP_TO_CORP,
  INDEPENDENT_CONTRACTOR
} from '@/lib/constants/employeeTypes'
import { HOURLY, MONTHLY, ANNUAL } from '@/lib/constants/rateTypes'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { FormattedNumberField } from '@/components/shared/textFields'
import { MessageBox } from '@/components/shared/messages'
import { TimeSheetTypesDropdown } from '@/components/shared/dropdowns'
import MarkupCalculator from '@/components/assignments/MarkupCalculator.vue'
import { BillRateTypesDropdownList } from '@/components/billRateTypes'
import { PayRateTypesDropdownList } from '@/components/payRateTypes'
import {
  CONTRACT_TO_HIRE,
  LENGTH_OF_CONTRACT_DEFAULT,
  CONVERSION_FEE_DEFAULT
} from '@/lib/constants/contractTypeDefault'
import {
  getOverheadPercent,
  getMonthlyTotalPayRate,
  getMonthlyNetBillRate,
  getMarkup,
  getExpensesDeduction
} from '@/lib/grossProfitCalculator'
import { formatCurrency, formatPercent, getEmployeeTypeLabel } from '@/lib/display'

export default {
  components: {
    CommissionsCollector,
    SimpleDatePicker,
    ContractTypesDropDownList,
    PayFrequenciesDropdownList,
    ExpensesCard,
    FormattedNumberField,
    MessageBox,
    MarkupCalculator,
    PayRateTypesDropdownList,
    BillRateTypesDropdownList,
    TimeSheetTypesDropdown
  },
  props: {
    assignment: {
      type: Object,
      required: true
    },
    vmsFee: {
      type: Number,
      required: false,
      default: 0
    }
  },
  data: () => ({
    HOURLY,
    MONTHLY,
    ANNUAL,
    EMPLOYEE_WITH_BENEFITS,
    EMPLOYEE_NO_BENEFITS,
    CORP_TO_CORP,
    INDEPENDENT_CONTRACTOR,
    tab: null,
    CONTRACT_DETAILS: 0,
    COMMISSIONS: 1,
    EXPENSES: 2,
    isFormValid: false,
    monthlyExpensesDeduction: 0
  }),
  computed: {
    ...mapFields('settings', ['payRateSettings']),
    isContractToHire () {
      return this.assignment.contractTypeId === CONTRACT_TO_HIRE
    },
    isC2C () {
      return this.assignment.employeeTypeId === CORP_TO_CORP
    },
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
    },
    canSetContractorCommission () {
      return this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    isHourlyPaid () {
      return this.assignment.payRateTypeId === HOURLY
    },
    totalPayRate () {
      if (this.assignment.payRate !== null && this.assignment.payRateTypeId && this.assignment.employeeTypeId && this.assignment.workedHoursAverage !== null) {
        return getMonthlyTotalPayRate(getOverheadPercent(this.assignment.employeeTypeId, this.payRateSettings), this.assignment.payRate, this.assignment.payRateTypeId, this.assignment.workedHoursAverage)
      }
      return null
    },
    netBillRate () {
      if (this.vmsFee !== null && this.assignment.billRate !== null && this.assignment.billRateTypeId) {
        return getMonthlyNetBillRate(this.vmsFee, this.assignment.billRate, this.assignment.billRateTypeId, this.assignment.workedHoursAverage)
      }
      return null
    },
    grossProfit () {
      if (this.netBillRate && this.totalPayRate) {
        return this.netBillRate - this.totalPayRate - this.monthlyExpensesDeduction
      }
      return null
    },
    totalPayRateHint () {
      if (this.totalPayRate) {
        return `Total Pay Rate = Pay Rate + ${getEmployeeTypeLabel(this.assignment.employeeTypeId)} Overhead (${formatPercent(getOverheadPercent(this.assignment.employeeTypeId, this.payRateSettings))})`
      }
      return 'Total Pay Rate = Pay Rate + Overhead'
    },
    netBillRateHint () {
      if (this.netBillRate) {
        return `Net Bill Rate = Bill Rate - VMS Fee (${formatPercent(this.vmsFee)})`
      }
      return 'Net Bill Rate = Bill Rate - VMS Fee'
    },
    grossProfitHint () {
      let hint = 'Gross Profit = Net Bill Rate - Total Pay Rate'
      if (this.monthlyExpensesDeduction > 0) {
        hint += ` - Monthly Expenses (${formatCurrency(this.monthlyExpensesDeduction)})`
      }
      return hint
    },
    markupPercentage () {
      if (this.totalPayRate && this.grossProfit) {
        return getMarkup(this.totalPayRate, this.grossProfit)
      }
      return null
    }
  },
  watch: {
    'assignment.defaultExpensesByClient': function () {
      if (this.assignment.expenses && this.assignment.expenses.length > 0) {
        const defaultExpenses = this.assignment.expenses.filter(expense => !!expense.clientDefaultExpense)
        for (let defaultExpense of defaultExpenses) {
          const indexToDelete = this.assignment.expenses.findIndex(expense => expense.id === defaultExpense.id && !!expense.clientDefaultExpense)
          if (indexToDelete > -1) {
            this.assignment.expenses.splice(indexToDelete, 1)
          }
        }
      }

      for (let index = this.assignment.defaultExpensesByClient.length - 1; index >= 0; index--) {
        this.assignment.expenses.unshift({ ...this.assignment.defaultExpensesByClient[index], clientDefaultExpense: true })
      }
    },
    'assignment.expenses': {
      deep: true,
      handler () {
        this.monthlyExpensesDeduction = getExpensesDeduction(this.assignment.expenses, this.MONTHLY, this.assignment.workedHoursAverage)
      }
    }
  },
  methods: {
    userHasRoles,
    getCurrentUser,
    Required,
    // Exposes validation reset to the parent
    resetValidation () {
      this.$refs.form.resetValidation()
    },
    // Exposes validation to the parent
    validate () {
      const isFormValid = this.$refs.form.validate()
      const hasRequiredFields = this.checkRequiredFields()
      const commissionsValid = this.$refs.commissionCollector
        ? this.$refs.commissionCollector.validateCommissionsPercentages()
        : true

      if (!isFormValid || !hasRequiredFields || !commissionsValid) {
        this.$refs.messageBox.show()
        return false
      }
      return true
    },
    checkRequiredFields () {
      return this.assignment.billRate &&
        this.assignment.startAt &&
        this.assignment.employeeTypeId &&
        this.assignment.billRateTypeId &&
        this.assignment.payFrequencyId &&
        this.assignment.payRateTypeId &&
        this.assignment.payRate &&
        this.assignment.workedHoursAverage &&
        (this.isContractToHire ? (this.assignment.lengthOfContract !== null && this.assignment.conversionFee !== null && this.assignment.endAt) : true) &&
        (this.isC2C ? (this.assignment.ein && this.assignment.corporateName) : true)
    },
    ...mapActions('settings', ['getSettings']),
    ...mapActions('client', ['getClient']),
    ensureAssignmentDefaults () {
      const defaults = {
        active: true,
        userId: null,
        clientId: null,
        billRate: null,
        startAt: null,
        endAt: null,
        contractTypeId: null,
        lengthOfContract: LENGTH_OF_CONTRACT_DEFAULT,
        conversionFee: CONVERSION_FEE_DEFAULT,
        employeeTypeId: null,
        corporateName: null,
        overtimeEligible: false,
        billRateTypeId: null,
        payFrequencyId: null,
        payRateTypeId: null,
        payRate: null,
        ein: null,
        commissions: [],
        expenses: [],
        defaultExpensesByClient: [],
        vmsFee: 0,
        workedHoursAverage: null,
        jobTitle: null,
        timeSheetType: null,
        clockIPAddress: null,
        overtimeThreshold: null,
        overtimeRate: null,
        contractorCommissionEnabled: false,
        contractorCommissionMarkup: null
      }

      Object.keys(defaults).forEach((key) => {
        if (typeof this.assignment[key] === 'undefined' || this.assignment[key] === null) {
          this.$set(this.assignment, key, defaults[key])
        }
      })
    },
    handleCommissionsChange (value) {
      this.assignment.commissions = value
    },
    isLengthOfContractRequired () {
      return this.isContractToHire ? (this.assignment.lengthOfContract === null ? 'Length of Contract is Required' : true) : true
    },
    isConversionFeeRequired () {
      return this.isContractToHire ? (this.assignment.conversionFee === null ? 'Conversion Fee is Required' : true) : true
    },
    isCorporateNameRequired () {
      return this.isC2C ? (!this.assignment.corporateName ? 'Corporate Name is Required' : true) : true
    },
    isEINRequired () {
      return this.isC2C ? (!this.assignment.ein ? 'EIN is Required' : true) : true
    },
    isFilledOut () {
      return this.assignment.employeeTypeId ? true : 'Please choose the consultant type'
    },
    endDateChangeHandler (endDate) {
      this.assignment.endAt = endDate
      if (!endDate) {
        this.assignment.active = true
        return
      }
      const tempEndDate = endDate.split('-')
      const today = new Date().setHours(0, 0, 0, 0)
      if (new Date(tempEndDate[0], parseInt(tempEndDate[1]) - 1, tempEndDate[2]) <= today) {
        this.assignment.active = false
      }
    },
    setPayRateType () {
      if (this.assignment.payRate && !this.assignment.payRateTypeId) {
        if (this.assignment.payRate < 500) {
          this.assignment.payRateTypeId = HOURLY
        } else if (this.assignment.payRate < 19999) {
          this.assignment.payRateTypeId = MONTHLY
        } else if (this.assignment.payRate >= 20000) {
          this.assignment.payRateTypeId = ANNUAL
        }
      }
    },
    setBillRateType () {
      if (this.assignment.billRate && !this.assignment.billRateTypeId) {
        if (this.assignment.billRate < 500) {
          this.assignment.billRateTypeId = HOURLY
        } else if (this.assignment.billRate < 19999) {
          this.assignment.billRateTypeId = MONTHLY
        } else if (this.assignment.billRate >= 20000) {
          this.assignment.billRateTypeId = ANNUAL
        }
      }
    },
    payRateTypeChangeHandler (newValue) {
      if (newValue === MONTHLY || newValue === ANNUAL) {
        this.assignment.overtimeEligible = false
      }
      this.assignment.payRateTypeId = newValue
    },
    openMarkupCalculator () {
      this.$refs.markupCalculator.showModal()
    }
  },
  created () {
    this.getSettings().then(() => {
      this.ensureAssignmentDefaults()
      if (this.assignment.clientId) {
        this.getClient({ id: this.assignment.clientId }).then((client) => {
          this.assignment.vmsFee = client.vmsFee
          this.assignment.defaultExpensesByClient = client.contractorExpenses.filter(expense => expense.active === true)
        })
      }
      if (this.assignment.workedHoursAverage === null) {
        this.assignment.workedHoursAverage = this.payRateSettings.workedHoursAverageDefault
      }
      if (this.assignment.overtimeThreshold === null) {
        this.assignment.overtimeThreshold = this.payRateSettings.overtimeThresholdDefault
      }
      if (this.assignment.overtimeRate === null) {
        this.assignment.overtimeRate = this.payRateSettings.overtimeRateDefault
      }
    })
  }
}
</script>
